import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import StepContainer from '../../components/StepContainer';
import Button from '../../components/Button';
import { useBuildAudience } from '../../context/BuildAudienceContext';
import { UI_TEXT, ERROR_MESSAGES } from '../../config/ui-text';
import { API_URL, ENDPOINTS } from '../../config/api';

interface AudienceSegmentationApproachProps {
  onNext: () => void;
  onBack: () => void;
}

const AudienceSegmentationApproach: React.FC<AudienceSegmentationApproachProps> = ({
  onNext,
  onBack,
}) => {
  const { buildAudienceData, updateBuildAudienceData } = useBuildAudience();

  const [segmentationApproach, setSegmentationApproach] = useState(buildAudienceData.segmentationApproach || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleBuildAudience = async () => {
    setIsSubmitting(true);
    setApiError('');

    try {
      // Save segmentation approach to context
      updateBuildAudienceData({
        segmentationApproach: segmentationApproach.trim(),
      }, 3);

      // Prepare complete audience data for submission
      const completeAudienceData = {
        audience_request: {
          website: buildAudienceData.websiteUrl,
          segmentation_focus: buildAudienceData.segmentationFocus,
          segmentation_approach: segmentationApproach.trim()
        }
      };

      // Send data to submission endpoint - backend will handle segment generation
      const response = await fetch(`${API_URL}${ENDPOINTS.AUDIENCE.FLOW.SUBMIT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(completeAudienceData),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errordata.error || data.detail || 'Failed to submit audience');
      }

      const result = await response.json();

      // Store taskId in context for finalize page
      if (result.task_id) {
        updateBuildAudienceData({ submissionTaskId: result.task_id });
      }

      // Navigate to finalize page to show submission status
      onNext();
    } catch (error) {
      console.error('Error submitting audience:', error);
      setApiError(error instanceof Error ? error.message : ERROR_MESSAGES.SUBMIT_AUDIENCE_FAILED);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSegmentationApproachChange = (value: string) => {
    setSegmentationApproach(value);
    // Update context to invalidate dependent data if needed
    updateBuildAudienceData({
      segmentationApproach: value.trim(),
    }, 3);
  };

  return (
    <StepContainer
      title=""
      className="animate-fadeIn flex items-center justify-center min-h-[70vh]"
    >
      <div className="max-w-3xl mx-auto space-y-8 w-full">
        <div className="space-y-6">
          {/* Main Question */}
          <div className="space-y-2">
            <p className="text-sm text-gray-400 font-light mb-3">Optional</p>
            <h1 className="text-2xl font-normal text-gray-900 leading-relaxed">
              {UI_TEXT.QUESTIONNAIRE.STEP2.TITLE}
            </h1>
            <p className="text-base font-light text-gray-400 leading-relaxed">
              {UI_TEXT.QUESTIONNAIRE.STEP2.DESCRIPTION}
            </p>
          </div>

          {/* Input Field */}
          <div className="space-y-3">
            <input
              type="text"
              className={`w-full px-0 py-2 bg-transparent border-0 border-b-2 transition-all duration-200 text-2xl font-light placeholder:text-sm placeholder-gray-300 outline-none ring-0 focus:ring-0 focus:outline-none ${
                segmentationApproach
                  ? 'border-b-primary'
                  : 'border-b-gray-200 hover:border-b-gray-300'
              } focus:border-b-primary`}
              placeholder={UI_TEXT.QUESTIONNAIRE.STEP2.PLACEHOLDER}
              value={segmentationApproach}
              onChange={(e) => handleSegmentationApproachChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleBuildAudience();
                }
              }}
              disabled={isSubmitting}
            />

            {/* Error Message */}
            {apiError && (
              <p className="text-sm font-light text-red-400">
                {apiError}
              </p>
            )}
          </div>
        </div>

        {/* Navigation buttons - right below the input */}
        <div className="flex items-center space-x-4">
          <Button
            variant="primary"
            onClick={handleBuildAudience}
            disabled={isSubmitting}
            className="font-light"
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {UI_TEXT.LOADING.BUILDING}
              </span>
            ) : (
              UI_TEXT.BUTTONS.BUILD_AUDIENCE
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={onBack}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 font-light"
          >
            {UI_TEXT.BUTTONS.BACK}
          </Button>
        </div>
      </div>
    </StepContainer>
  );
};

export default AudienceSegmentationApproach;